import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Spinner,
  Tab,
  TabList,
} from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import type { SugerirConceptosResponse } from '@supernova/shared';
import { listCatalogResources, type CatalogResource } from '../../api/catalogs.js';

/** Lo que se elige: un concepto de uno de los catálogos del proyecto. */
export interface ConceptoParaCambiar {
  catalogId: string;
  clave: string;
  descripcion: string;
  unidad: string | null;
  precio: number | null;
  /** Por qué se recomienda (solo en «Recomendados»). */
  razon?: string;
}

/**
 * «Cambiar concepto» de un renglón de la hoja de planos: busca en los catálogos del proyecto (por
 * prioridad, sin IA, gratis) o propone (Recomendados, el mismo motor que al elegir el concepto la
 * primera vez; se piden solo al abrir esa pestaña) y devuelve el elegido. El renglón conserva su cantidad,
 * partida y selección del plano; eso lo hace el servidor.
 */
export function CambiarConceptoDialog({
  abierto,
  descripcionActual,
  unidadActual,
  catalogos,
  guardando,
  formatoPrecio,
  recomendar,
  onElegir,
  onCerrar,
}: {
  abierto: boolean;
  descripcionActual: string;
  unidadActual: string | null;
  /** Catálogos del proyecto por prioridad (índice 0 = mayor). */
  catalogos: string[];
  guardando: boolean;
  /** El mismo formato de dinero que la hoja (moneda de la obra). */
  formatoPrecio: (n: number) => string;
  /** Pide los recomendados para lo que el renglón mide (la pantalla arma la selección). */
  recomendar: () => Promise<SugerirConceptosResponse>;
  onElegir: (c: ConceptoParaCambiar) => void;
  onCerrar: () => void;
}) {
  const [q, setQ] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [resultados, setResultados] = useState<ConceptoParaCambiar[]>([]);
  const [pestana, setPestana] = useState<'buscar' | 'recomendados'>('buscar');
  const [recomendados, setRecomendados] = useState<ConceptoParaCambiar[] | null>(null);
  const [notaRecomendados, setNotaRecomendados] = useState<string | null>(null);

  useEffect(() => {
    if (!abierto) return;
    setQ('');
    setResultados([]);
    setPestana('buscar');
    setRecomendados(null);
    setNotaRecomendados(null);
  }, [abierto]);

  // Los recomendados se piden recién al entrar a su pestaña (pueden consultar a la IA): abrir el
  // diálogo para buscar no consume nada.
  useEffect(() => {
    if (!abierto || pestana !== 'recomendados' || recomendados != null) return;
    let vigente = true;
    recomendar()
      .then((res) => {
        if (!vigente) return;
        setNotaRecomendados(res.notaFaltante ?? res.nota ?? null);
        setRecomendados(
          res.sugerencias
            // El concepto que el renglón ya tiene no se recomienda como «otro» (igual que en «Otra opción»).
            .filter((x) => x.descripcion.trim().toLowerCase() !== descripcionActual.trim().toLowerCase())
            .map((x) => ({
              catalogId: x.catalogId, clave: x.clave, descripcion: x.descripcion, unidad: x.unidad,
              precio: x.precio, razon: x.razonModelo,
            })),
        );
      })
      .catch(() => {
        if (!vigente) return;
        setRecomendados([]);
        setNotaRecomendados('No pude traer recomendados. Búscalo en «Buscar».');
      });
    return () => {
      vigente = false;
    };
    // `recomendar` cambia en cada render del padre: se pide una vez por apertura.
  }, [abierto, pestana]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const texto = q.trim();
    if (!abierto || texto.length < 2 || catalogos.length === 0) {
      setResultados([]);
      return;
    }
    let vigente = true;
    const t = setTimeout(() => {
      setBuscando(true);
      void Promise.all(
        catalogos.map((cid) =>
          listCatalogResources(cid, { q: texto, tipos: [7], limit: 15, todasLasPalabras: true })
            .then((r) => r.resources.map((x: CatalogResource) => ({ ...x, catalogId: cid })))
            .catch(() => [] as (CatalogResource & { catalogId: string })[]),
        ),
      ).then((listas) => {
        if (!vigente) return;
        const vistos = new Set<string>();
        const out: ConceptoParaCambiar[] = [];
        for (const lista of listas) {
          for (const r of lista) {
            if (vistos.has(r.id)) continue;
            vistos.add(r.id);
            out.push({ catalogId: r.catalogId, clave: r.clave, descripcion: r.descripcion, unidad: r.unidad, precio: r.precio });
          }
        }
        setResultados(out.slice(0, 30));
        setBuscando(false);
      });
    }, 300);
    return () => {
      vigente = false;
      clearTimeout(t);
    };
  }, [q, abierto, catalogos]);

  const mismaUnidad = (u: string | null) => (u ?? '').trim().toLowerCase() === (unidadActual ?? '').trim().toLowerCase();

  const opcion = (r: ConceptoParaCambiar) => (
    <button
      key={`${r.catalogId}:${r.clave}`}
      type="button"
      disabled={guardando}
      onClick={() => onElegir(r)}
      style={{
        textAlign: 'left', padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
        border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', color: 'var(--text-primary)',
        display: 'flex', flexDirection: 'column', gap: 2,
      }}
    >
      <span style={{ fontSize: 13 }}>{r.descripcion}</span>
      <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
        {r.clave} · {r.unidad ?? 'sin unidad'}
        {r.precio != null ? ` · ${formatoPrecio(r.precio)}` : ' · sin precio'}
        {!mismaUnidad(r.unidad) && (
          <b style={{ color: 'var(--warn, #b26a00)' }}> · otra unidad: revisa la cantidad</b>
        )}
      </span>
      {r.razon && <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{r.razon}</span>}
    </button>
  );

  return (
    <Dialog open={abierto} onOpenChange={(_, d) => { if (!d.open && !guardando) onCerrar(); }}>
      <DialogSurface style={{ maxWidth: 640 }}>
        <DialogBody>
          <DialogTitle>Cambiar el concepto de este renglón</DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
              Ahora: <b style={{ color: 'var(--text-primary)' }}>{descripcionActual}</b>
              {unidadActual ? ` (${unidadActual})` : ''}.
            </span>
            <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)', lineHeight: 1.45 }}>
              Elige otro concepto de tus catálogos: toma su lugar en la hoja, con su precio. La cantidad, la
              partida y lo que elegiste del plano se quedan igual.
            </span>
            <TabList selectedValue={pestana} onTabSelect={(_, d) => setPestana(d.value as typeof pestana)} size="small">
              <Tab value="buscar">Buscar</Tab>
              <Tab value="recomendados">Recomendados</Tab>
            </TabList>
            {pestana === 'buscar' && (
              <Input
                autoFocus
                contentBefore={<SearchRegular />}
                placeholder="Busca en tus catálogos: «muro block 15», «firme concreto»…"
                value={q}
                onChange={(_, d) => setQ(d.value)}
              />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 360, overflow: 'auto' }}>
              {pestana === 'recomendados' ? (
                <>
                  {recomendados == null && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
                      <Spinner size="tiny" /> Buscando lo que mejor le queda…
                    </span>
                  )}
                  {notaRecomendados && (
                    <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.45 }}>{notaRecomendados}</span>
                  )}
                  {recomendados != null && recomendados.length === 0 && !notaRecomendados && (
                    <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>No hay recomendados. Búscalo en «Buscar».</span>
                  )}
                  {(recomendados ?? []).map((r) => opcion(r))}
                </>
              ) : (
                <>
                  {buscando && <Spinner size="tiny" />}
                  {!buscando && q.trim().length >= 2 && resultados.length === 0 && (
                    <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>No encontré conceptos con esas palabras.</span>
                  )}
                  {resultados.map((r) => opcion(r))}
                </>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            {guardando && <Spinner size="tiny" />}
            <Button appearance="secondary" disabled={guardando} onClick={onCerrar}>Cancelar</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
